import React from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  TableCell,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CloudSync as CloudSyncIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SortableTable from "../components/SortableTable";
import ProductDetailModal from "../components/ProductDetailModal";
import { formatPrice } from "../utils/formatPrice";
import { useProductManagement, getStatusColor, getStatusLabel } from "../hooks/useProductManagement";

function ProductManagement() {
  const {
    productsData,
    productsLoading,
    productsError,
    refetchProducts,
    eshops,
    manufacturers,
    categories,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    searchTerm,
    handleSearch,
    selectedEshop,
    setSelectedEshop,
    selectedStatus,
    setSelectedStatus,
    selectedManufacturer,
    setSelectedManufacturer,
    selectedCategory,
    setSelectedCategory,
    selectedProductNumber,
    setSelectedProductNumber,
    selectedSku,
    setSelectedSku,
    selectedProductIds,
    handleSelectionChange,
    tableResetKey,
    selectedProduct,
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    exportProductsMutation,
    syncSheetsMutation,
    syncSnackbar,
    handleCloseSyncSnackbar,
    handleExportCSV,
    handleExportXLSX,
    handleSyncSheets,
    deleteProductsMutation,
    isDeleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleConfirmDelete,
    deleteSnackbar,
    handleCloseDeleteSnackbar,
  } = useProductManagement();

  if (productsLoading && !productsData) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const products = productsData?.items || [];
  const totalCount = productsData?.total || 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Product Management
        </Typography>
        <Box display="flex" gap={1}>
          {selectedProductIds.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
              disabled={deleteProductsMutation.isLoading}
            >
              Delete Selected ({selectedProductIds.size})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={exportProductsMutation.isLoading}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportXLSX}
            disabled={exportProductsMutation.isLoading}
          >
            Export XLSX
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudSyncIcon />}
            onClick={handleSyncSheets}
            disabled={syncSheetsMutation.isLoading}
          >
            Sync Google Sheets
          </Button>
        </Box>
      </Box>

      {productsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading products: {productsError.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Products
              </Typography>
              <Typography variant="h4">{totalCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Products
              </Typography>
              <Typography variant="h4">
                {productsData?.stats?.active || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Not Found
              </Typography>
              <Typography variant="h4">
                {productsData?.stats?.not_found || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                E-shops
              </Typography>
              <Typography variant="h4">
                {new Set(products.map((p) => p.eshop_id)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Product Name"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Product Number"
              placeholder="Filter by PN..."
              value={selectedProductNumber}
              onChange={(e) => setSelectedProductNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="SKU / Model"
              placeholder="Filter by SKU..."
              value={selectedSku}
              onChange={(e) => setSelectedSku(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>E-shop</InputLabel>
              <Select
                label="E-shop"
                value={selectedEshop}
                onChange={(e) => setSelectedEshop(e.target.value)}
              >
                <MenuItem value="">All E-shops</MenuItem>
                {eshops?.map((eshop) => (
                  <MenuItem key={eshop.id} value={eshop.id}>
                    {eshop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="not_found">Not Found</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Manufacturer</InputLabel>
              <Select
                label="Manufacturer"
                value={selectedManufacturer}
                onChange={(e) => setSelectedManufacturer(e.target.value)}
              >
                <MenuItem value="">All Manufacturers</MenuItem>
                {manufacturers.map((manufacturer) => (
                  <MenuItem key={manufacturer} value={manufacturer}>
                    {manufacturer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => refetchProducts()}
                disabled={productsLoading}
                size="medium"
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <SortableTable
        key={tableResetKey}
        data={products}
        columns={[
          { id: "product_name", label: "Product Name" },
          { id: "product_number", label: "Product Number" },
          { id: "eshop_name", label: "E-shop", sortable: false },
          { id: "manufacturer", label: "Manufacturer" },
          { id: "category", label: "Category" },
          { id: "model_sku", label: "SKU" },
          { id: "currency", label: "Currency" },
          { id: "price_per_piece", label: "Price" },
          { id: "price_per_wp", label: "Price/Wp" },
          { id: "stock_quantity", label: "Stock" },
          { id: "status", label: "Status" },
          { id: "last_updated", label: "Last Updated" },
          { id: "actions", label: "Actions", sortable: false },
        ]}
        enableSelection={true}
        onSelectionChange={handleSelectionChange}
        order={sortOrder}
        orderBy={sortBy}
        onSortChange={handleSortChange}
        renderRow={(product) => (
          <TableRow key={product.id}>
            <TableCell>{product.product_name}</TableCell>
            <TableCell>{product.product_number || "N/A"}</TableCell>
            <TableCell>{product.eshop_name}</TableCell>
            <TableCell>{product.manufacturer}</TableCell>
            <TableCell>{product.category || "N/A"}</TableCell>
            <TableCell>{product.model_sku}</TableCell>
            <TableCell>{product.currency || "CZK"}</TableCell>
            <TableCell>{formatPrice(product.price_per_piece, product.currency)}</TableCell>
            <TableCell>{formatPrice(product.price_per_wp, product.currency)}</TableCell>
            <TableCell>{product.stock_quantity}</TableCell>
            <TableCell>
              <Chip
                label={getStatusLabel(product.status)}
                color={getStatusColor(product.status)}
                size="small"
              />
            </TableCell>
            <TableCell>
              {product.last_updated
                ? new Date(product.last_updated).toLocaleString("cs-CZ", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "N/A"}
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleOpenModal(product)}
              >
                Details
              </Button>
            </TableCell>
          </TableRow>
        )}
      />

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <ProductDetailModal
        product={selectedProduct}
        open={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {selectedProductIds.size} selected product(s)? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteProductsMutation.isLoading}
          >
            {deleteProductsMutation.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Snackbar */}
      <Snackbar
        open={deleteSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseDeleteSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseDeleteSnackbar}
          severity={deleteSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {deleteSnackbar.message}
        </MuiAlert>
      </Snackbar>
      {/* Sync Snackbar */}
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSyncSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseSyncSnackbar}
          severity={syncSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {syncSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default ProductManagement;
